import Head from "next/head";
import React, { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import styles from "./solutions.module.scss";
import { svgImages } from "@/lib/svgImages";
import Image from "next/image";
import HeroGradientTextWithIcon from "@/components/heroGradientTextWithIcon/heroGradientTextWithIcon";
import FeaturesSwiper from "@/components/featuresSwiper/featuresSwiper";
import FaqWithCta from "@/components/faqWithCta/faqWithCta";
import Link from "next/link";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CustomSpinner from "@/components/spinner/spinner";
import TitleWithDescriptionV2 from "@/components/titleWithDescriptionV2/titleWithDescriptionV2";
import type { Swiper as SwiperType } from "swiper";
import { Navigation } from "swiper/modules";
import { Pagination } from "swiper/modules";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/navigation";
import { useWordPressApi } from "@/hooks/useWordPressApi";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

const Solutions = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [currentUrl, setCurrentUrl] = useState("");
  const [domainUrl, setDomainUrl] = useState("");
  const cardsContainerRef = useRef(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const { getSolutions, currentLanguage } = useWordPressApi();

  useEffect(() => {
    if (window.location.href) {
      setCurrentUrl(window.location.href);
      setDomainUrl(window.location.hostname);
    }
  }, []);

  const [solutionsData, setSolutionsData] = useState(null);
  useEffect(() => {
    (async () => {
      const data = await getSolutions();
      setSolutionsData(data);
      setLoading(false);
    })();
  }, [currentLanguage]);

  // Setup scroll animation
  useEffect(() => {
    if (!cardsContainerRef.current || !solutionsData) return;

    const cards = gsap.utils.toArray(`.${styles.solutionsCard__link}`);

    // Set initial state for all cards
    // gsap.set(cards, { opacity: 0, y: 10 });

    // Create animation for each card with stagger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: cardsContainerRef.current,
        start: "top 80%",
        end: "bottom 20%",
        toggleActions: "play none none reverse",
      },
    });

    tl.from(cards, {
      opacity: 0,
      y: 10,
      duration: 0.8,
      stagger: 0.2,
      ease: "power2.out",
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [solutionsData]);

  const baseUrl = `https://${domainUrl || "qrhub.io"}`;
  const defaultOgImage = `${baseUrl}/images/featured_images/qrhub-featured.jpg`;
  const defaultOgUrl = `${baseUrl}${router.asPath || ""}`;

  return (
    <>
      <Head>
        <title>QRHUB - Solutions</title>
        <meta name="description" content="Empower your business with tailored QR code Solutions" />
        <meta property="og:title" content="QRHUB - Solutions" />
        <meta property="og:description" content="Empower your business with tailored QR code Solutions" />
        <meta property="og:image" content={defaultOgImage} />
        <meta property="og:url" content={defaultOgUrl} />
      </Head>
      {loading ? (
        <>
          <CustomSpinner />
        </>
      ) : (
        <>
          <Head>
            <title>QRHUB - Solutions</title>
            <meta
              name="description"
              content="Empower your business with tailored QR code Solutions"
            ></meta>
            <meta property="og:title" content="QRHUB - Solutions"></meta>
            <meta
              property="og:description"
              content="Empower your business with tailored QR code Solutions"
            ></meta>
            <meta
              property="og:image"
              content={defaultOgImage}
            ></meta>
            <meta property="og:url" content={currentUrl}></meta>
          </Head>
          <div className={`${styles["solutions"]}`}>
            <HeroGradientTextWithIcon
              subtitle="Dynamic QR"
              title="Solutions"
              description="Personal to Enterprise"
              image={
                <Image
                  className={`${styles[""]}`}
                  src={`/images/solutions/solutions.svg`}
                  width={311}
                  height={311}
                  alt={`QRHUB Solutions`}
                />
              }
              buttonTitle={`Get Started`}
              buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL}
              target={"_blank"}
              isProduct={false}
              isSolution={false}
              isStatic={false}
            />

            <div className={`custom-container`}>
              <div className={`inner`}>
                <TitleWithDescriptionV2
                  title={`Simplify Access <br /> and Boost Engagement`}
                  description={`QRHUB offers versatile and customizable QR code solutions
                      that enhance customer engagement, streamline operations,
                      and provide seamless access to information. No matter your
                      industry, our dynamic QR codes are designed to meet your
                      specific needs. Explore how QRHUB can transform your
                      business by making it easier to share resources, services,
                      and contact details efficiently.`}
                  light={false}
                />
                <div
                  className={`${styles["solutionsCards"]} d-flex-md d-none-xs`}
                  ref={cardsContainerRef}
                >
                  {solutionsData &&
                    solutionsData.map((item) => (
                      <Link
                        href={`/en/solutions/${item.slug}/`}
                        key={item.id}
                        className={`${styles["solutionsCard__link"]}`}
                      >
                        <div className={`${styles["solutionsCard"]}`}>
                          <h3 className={`font-size-32`}>
                            {item?.acf.hero_title}
                          </h3>
                          <div
                            className={`${styles["solutionsCard__description"]}`}
                          >
                            <p
                              className={`font-size-20`}
                              dangerouslySetInnerHTML={{
                                __html: item?.acf.hero_description,
                              }}
                            />
                            <div className={`${styles["buttonContainer"]}`}>
                              <div className={`${styles["button"]}`}>
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: svgImages.arrowRight,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
                <div
                  className={`${styles["solutionsCardsMobile"]} d-none-md d-flex-xs`}
                >
                  <div className={styles.sliderContainer}>
                    <Swiper
                      modules={[Navigation, Autoplay, Pagination]}
                      centeredSlides={false}
                      loop={false}
                      navigation={{
                        nextEl: `.${styles.swiperButtonNext}`,
                        prevEl: `.${styles.swiperButtonPrev}`,
                      }}
                      autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                        stopOnLastSlide: false,
                      }}
                      onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                      }}
                      pagination={{
                        el: `.${styles.swiperPagination}`,
                        clickable: true,
                      }}
                      breakpoints={{
                        0: {
                          slidesPerView: 1.1,
                          spaceBetween: 10,
                          autoplay: {
                            disableOnInteraction: true,
                            pauseOnMouseEnter: false,
                          },
                        },

                        480: {
                          slidesPerView: 2,
                          spaceBetween: 20,
                        },
                        1025: {
                          slidesPerView: 3,
                          spaceBetween: 30,
                        },
                      }}
                      className={styles.featuresSwiperElement}
                      initialSlide={0}
                      grabCursor={true}
                    >
                      {solutionsData &&
                        solutionsData?.map((slide) => (
                          <SwiperSlide
                            key={slide.id}
                            className={styles.swiperSlide}
                          >
                            <Link
                              href={`/en/solutions/${slide.slug}/`}
                              key={slide.id}
                              className={`${styles["solutionsCard__link"]}`}
                            >
                              <div className={`${styles["solutionsCard"]}`}>
                                <h3 className={`font-size-32`}>
                                  {slide?.acf.hero_title}
                                </h3>
                                <div
                                  className={`${styles["solutionsCard__description"]}`}
                                >
                                  <p
                                    className={`font-size-18`}
                                    dangerouslySetInnerHTML={{
                                      __html: slide?.acf.hero_description,
                                    }}
                                  />
                                  <div
                                    className={`${styles["buttonContainer"]}`}
                                  >
                                    <div className={`${styles["button"]}`}>
                                      Learn More
                                      <span
                                        className={`${styles.arrow}`}
                                        dangerouslySetInnerHTML={{
                                          __html: svgImages.navRight,
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                      className={`${styles.swiperPagination} d-none-md`}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <FeaturesSwiper />
            <FaqWithCta
              title="Ready to Get Started?"
              description="QRHUB can transform your business and connect you with your audience
        like never before."
              buttonTitle={`Sign Up Now`}
              buttonUrl={process.env.NEXT_PUBLIC_DASHBOARD_URL}
              target={"_blank"}
            />
          </div>
        </>
      )}
    </>
  );
};

export default Solutions;
